/**
 * @file activity-kinds.ts
 * @description Реестр видов событий сделки для кнопок «+ Заметка / + Задача /
 * + Звонок / + Встреча / + Письмо / + SMS» и формы добавления (phase19.31, SC61).
 *
 * Новый вид = запись: заголовок окна, подсказка в поле, нужен ли срок и
 * можно ли отметить выполненным. Заметку, письмо и SMS выполнять нечего —
 * их не зачёркиваем и кнопки «Выполнено» у них нет. У задачи, звонка и
 * встречи есть исполнитель (crm.md T1) — поле «Исполнитель» в форме.
 */

import { Mail, MessageSquare, NotebookPen, Phone, SquareCheck, Users, type LucideIcon } from "lucide-react";

import type { ActivityKind } from "../../../lib/api-crm";

export interface ActivityKindMeta {
  /** Подпись кнопки: «Заметка» → «+ Заметка». */
  label: string;
  /** Заголовок окна: «Новая заметка». */
  dialogTitle: string;
  /** Уведомление после сохранения: «Звонок добавлен». */
  addedText: string;
  placeholder: string;
  icon: LucideIcon;
  /** Есть срок (задача, звонок, встреча). */
  withDue: boolean;
  /** Можно отметить выполненным. */
  completable: boolean;
  /** Есть исполнитель — сотрудник магазина (crm.md T1): поле «Исполнитель» в форме. */
  assignable: boolean;
}

export const ACTIVITY_KIND_META: Record<ActivityKind, ActivityKindMeta> = {
  NOTE: { label: "Заметка", dialogTitle: "Новая заметка", addedText: "Заметка добавлена", placeholder: "Клиент просил без сахара", icon: NotebookPen, withDue: false, completable: false, assignable: false },
  TASK: { label: "Задача", dialogTitle: "Новая задача", addedText: "Задача добавлена", placeholder: "Подготовить коммерческое предложение", icon: SquareCheck, withDue: true, completable: true, assignable: true },
  CALL: { label: "Звонок", dialogTitle: "Новый звонок", addedText: "Звонок добавлен", placeholder: "Позвонить клиенту по поводу оплаты", icon: Phone, withDue: true, completable: true, assignable: true },
  MEETING: { label: "Встреча", dialogTitle: "Новая встреча", addedText: "Встреча добавлена", placeholder: "Встреча в офисе клиента", icon: Users, withDue: true, completable: true, assignable: true },
  EMAIL: { label: "Письмо", dialogTitle: "Новое письмо", addedText: "Письмо добавлено", placeholder: "Отправил прайс на почту", icon: Mail, withDue: false, completable: false, assignable: false },
  SMS: { label: "SMS", dialogTitle: "Новое SMS", addedText: "SMS добавлено", placeholder: "Напомнил о встрече", icon: MessageSquare, withDue: false, completable: false, assignable: false },
};

/**
 * Кнопки добавления — все виды на виду, без «⋯» (замечание владельца
 * 2026-10-02: «достань все кнопки из трёх точек»). Порядок — по частоте.
 */
export const COMPOSER_ACTIVITY_KINDS: ActivityKind[] = ["NOTE", "TASK", "CALL", "MEETING", "EMAIL", "SMS"];

/**
 * Срок из полей формы: «2026-10-05» + «14:30» → ISO в часовом поясе браузера.
 * Нет даты — срока нет; нет времени — конец рабочего дня 18:00.
 */
export function buildDueAt(date: string, time: string): string | undefined {
  if (!date) return undefined;
  const local = new Date(`${date}T${time || "18:00"}`);
  return Number.isNaN(local.getTime()) ? undefined : local.toISOString();
}
